'use client';

import React, { useMemo } from 'react';
import { Typography } from 'src/components';

/**
 * Props for the NewNoVolumeOverlay component
 */
interface NewNoVolumeOverlayProps {
  /** Array of column IDs used to calculate the width of the volume area */
  columnIds: number[];
}

/**
 * Displays an overlay when there are no rows to display in a table.
 * Shows a teal background on the right side where volume columns would be
 * and centers a "No rows to display" message in the remaining white area.
 */
const NewNoVolumeOverlay: React.FC<NewNoVolumeOverlayProps> = ({
  columnIds,
}) => {
  // Calculate the width of the volume area based on the number of columns
  const volumeAreaWidth = useMemo(() => {
    if (!columnIds?.length) return 200;

    const count = columnIds.length;
    // Different multiplier based on column count
    const multiplier = count > 1 ? 200 : 260;
    // Add padding for better appearance
    return count * multiplier + 56;
  }, [columnIds]);

  // Calculate the width of the white area (total width - volume area)
  const whiteAreaWidth = `calc(100% - ${volumeAreaWidth}px)`;

  return (
    <section
      className="flex flex-col items-center justify-center h-full w-full relative"
      aria-label="Empty table state"
    >
      {/* Volume area (teal background) */}
      <div
        className="absolute top-0 right-0 bottom-0 bg-teal-050 border-l border-teal-300 z-0 flex items-center justify-center px-4"
        style={{ width: `${volumeAreaWidth}px` }}
        aria-hidden={columnIds.length > 0}
      >
        {!columnIds.length && (
          <Typography className='text-secondary leading-tight font-["Inter"] text-sm text-center'>
            Add code set to see volume.
          </Typography>
        )}
      </div>

      {/* Main message - positioned for proper centering */}
      <div
        className="absolute left-0 flex justify-center"
        style={{ width: whiteAreaWidth }}
        role="status"
      >
        <Typography className='text-primary leading-tight font-["Inter"] text-base font-semibold'>
          No rows to display
        </Typography>
      </div>
    </section>
  );
};

export default NewNoVolumeOverlay;
